/**
 * Permission matching logic + dot-notation builder for the UI authorization layer.
 *
 * Permissions arrive from portal-api `/auth/me` as dot-notation slugs and may
 * contain trailing wildcards (e.g. "campaign.*", "brand.update.*"). A granted
 * wildcard covers every concrete permission under its prefix, so matching is
 * prefix-aware rather than a plain equality check.
 *
 * Instead of a hand-maintained catalog, permissions are written directly via
 * dot access on the exported service roots, e.g. `brand.create.CreateBrand`
 * resolves to the slug string "brand.create.CreateBrand".
 *
 *   <Can perm={[brand.create.CreateBrand]}>...</Can>
 *   <Can perm={[campaign['*']]}>...</Can>   // wildcard segment
 */

/** A fully-resolved permission slug, e.g. "brand.create.CreateBrand". */
export type Permission = string;

/**
 * A node in the dot-notation builder. Any property access yields another node,
 * and the node coerces to its accumulated dotted slug when used as a string.
 */
export interface PermPath {
  readonly [segment: string]: PermPath;
}

/** Anything accepted where a permission is expected: a slug or a builder node. */
export type PermInput = Permission | PermPath;

const createPermPath = (prefix: string): PermPath =>
  new Proxy({} as PermPath, {
    get(_target, key) {
      if (key === 'toString' || key === 'valueOf' || key === Symbol.toPrimitive) {
        return () => prefix;
      }
      if (typeof key === 'symbol') {
        return undefined;
      }
      return createPermPath(prefix ? `${prefix}.${key}` : key);
    },
  });

/** Resolve a permission input (slug or builder node) to its slug string. */
export const resolvePermission = (perm: PermInput): Permission => String(perm);

// Service roots — extend by adding a new root or via `createPermPath(...)`.
export const brand = createPermPath('brand');
export const user = createPermPath('user');
export const auth = createPermPath('auth');
export const identity = createPermPath('identity');
export const campaign = createPermPath('campaign');
export const consent = createPermPath('consent');
export const integration = createPermPath('integration');
export const agent = createPermPath('agent');
export const billing = createPermPath('billing');
// "billing-v2" is not a valid identifier, so its root is built explicitly.
export const billingV2 = createPermPath('billing-v2');
export const enrichment = createPermPath('enrichment');

export { createPermPath };

/**
 * Returns true if a single granted slug satisfies the required permission.
 * Supports trailing-wildcard grants: "campaign.*" grants "campaign.read.X",
 * and a bare "*" grants everything.
 */
export const permissionMatches = (granted: string, required: PermInput): boolean => {
  const req = resolvePermission(required);
  if (granted === req) return true;
  if (granted.endsWith('*')) {
    const base = granted.slice(0, -1); // "campaign.*" -> "campaign."
    return req.startsWith(base);
  }
  return false;
};

/**
 * Returns true if any of the granted slugs satisfies the required permission.
 */
export const hasPermission = (granted: readonly string[], required: PermInput): boolean =>
  granted.some((g) => permissionMatches(g, required));
